<script setup lang="ts">
import { onClickOutside } from '@vueuse/core'

export type MenuItem = {
  label: string
  onClick: () => void
  color?: 'danger'
}

const props = withDefaults(
  defineProps<{
    isActive: boolean
    menuItems: MenuItem[]
    width?: string
    anchor?: 'left' | 'right'
  }>(),
  {
    width: '200px',
    anchor: 'left',
  }
)

const emit = defineEmits<{
  (e: 'update:isActive', v: boolean): void
}>()

const parent = ref<HTMLElement | null>(null)

const close = () => {
  emit('update:isActive', false)
}

const toggle = () => {
  emit('update:isActive', !props.isActive)
}

onClickOutside(parent, () => {
  close()
})

const leftPosition = computed(() => (props.anchor === 'left' ? '8px' : 'auto'))
const rightPosition = computed(() => (props.anchor === 'left' ? 'auto' : '8px'))
</script>

<template>
  <div ref="parent" class="base-icon-menu">
    <button class="icon-block" @click="toggle">
      <slot name="icon" />
    </button>

    <Transition>
      <div v-show="isActive" class="content">
        <ul>
          <li v-for="(menu, i) in menuItems" :key="i">
            <button class="item" :class="{ danger: menu.color === 'danger' }" @click="menu.onClick">
              {{ menu.label }}
            </button>
          </li>
        </ul>
      </div>
    </Transition>
  </div>
</template>

<style scoped lang="scss">
.base-icon-menu {
  position: relative;
}

.icon-block {
  background: none;
  border: none;
  cursor: pointer;
}

.content {
  position: absolute;
  top: auto;
  left: v-bind(leftPosition);
  right: v-bind(rightPosition);
  margin-top: 12px;
  background: var(--semantic-tokens-neutral-default-background);
  border: 1px solid var(--semantic-tokens-neutral-default-border);
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 100;
  width: v-bind(width);

  ul {
    padding: 8px 0;
    margin: 0;
    list-style: none;
    .item {
      @include textToken.body-large;
      width: 100%;
      text-align: left;
      padding: 8px;
      background: none;
      border: none;
      cursor: pointer;
      &:hover {
        background: var(--semantic-tokens-neutral-xweak-surface);
      }
      &.danger {
        color: var(--semantic-tokens-danger-default-text);
      }
    }
  }
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.2s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
